// src/features/streams/StreamApi.ts

import { webApi } from '@/shared/api/WebApi';
import { ApiError } from '@/shared/api/ApiError';
import type { IListRes } from '@lib2/model/common/ListRes';
import type { StreamViewData, StreamCreateRequest, StreamMediaUpdateRequest, StreamSettingsUpdateRequest, RealtimeStreamData } from '@/lib/types/Stream';
import type {
  ISimulcastAllData,
  ISimulcastRtmpUpdateReq,
  ISimulcastFBCreateReq,
  ISimulcastFBUpdateReq,
  ISimulcastYTCreateReq,
  ISimulcastYTUpdateReq,
} from '@/lib/types/Simulcast';

export class StreamApi {
  /** Fetch all streams for a customer. */
  static async getStreams(cId: number): Promise<IListRes<StreamViewData>> {
    return webApi.get<IListRes<StreamViewData>>('media/videostream', { cId });
  }

  /** Fetch single stream by ID. Not-found/cross-account arrives as an empty 200 — normalized to a 404. */
  static async getStream(cId: number, mediaId: number): Promise<StreamViewData> {
    const res = await webApi.get<StreamViewData>(`media/videostream/${mediaId}`, { cId });
    return ApiError.requireFound(res, 'media', 'Stream');
  }

  /** Create a new stream. */
  static async createStream(cId: number, req: StreamCreateRequest): Promise<StreamViewData> {
    return webApi.post<StreamViewData>('media/videostream', { cId }, req);
  }

  /** Activate streams (batch). Uses shared media endpoint. */
  static async activateStreams(cId: number, mediaIds: number[]): Promise<void> {
    return webApi.post('media/activate', { cId }, { mediaIds });
  }

  /** Deactivate streams (batch). Uses shared media endpoint. */
  static async deactivateStreams(cId: number, mediaIds: number[]): Promise<void> {
    return webApi.post('media/deactivate', { cId }, { mediaIds });
  }

  /** Delete streams (batch). Uses shared media endpoint. */
  static async deleteStreams(cId: number, mediaIds: number[]): Promise<void> {
    return webApi.post('media/delete', { cId }, { mediaIds });
  }

  /** Update media fields (general information). */
  static async updateMedia(cId: number, mediaId: number, req: StreamMediaUpdateRequest): Promise<StreamViewData> {
    return webApi.post<StreamViewData>(`media/videostream/${mediaId}/media`, { cId }, req);
  }

  /** Update stream settings (recording, offline, etc.). */
  static async updateStream(cId: number, mediaId: number, req: StreamSettingsUpdateRequest): Promise<StreamViewData> {
    return webApi.post<StreamViewData>(`media/videostream/${mediaId}/stream`, { cId }, req);
  }

  /** Inject ad break into live stream. */
  static async injectAdBreak(cId: number, mediaId: number, duration: number): Promise<void> {
    return webApi.post('media/stream/inject-adbreak', { cId }, { mediaId, duration });
  }

  /** Get realtime stream statuses (live/recording detection). */
  static async getRealtimeStreams(cId: number): Promise<IListRes<RealtimeStreamData>> {
    return webApi.get<IListRes<RealtimeStreamData>>('media/stream/stat/realtime-stream', { cId });
  }

  // --- Sub-account consolidated endpoints (master accounts; scoped server-side to the login account's children) ---

  /** Fetch all active streams across all sub-accounts. Requires subcustomerRead. */
  static async getSubAccountStreams(): Promise<IListRes<StreamViewData>> {
    return webApi.get<IListRes<StreamViewData>>('media/subaccount-videostream');
  }

  /** Get realtime stream statuses across all sub-accounts. Requires subcustomerRead. */
  static async getSubAccountRealtimeStreams(): Promise<IListRes<RealtimeStreamData>> {
    return webApi.get<IListRes<RealtimeStreamData>>('media/stream/stat/subaccount-realtime-stream');
  }

  // --- Simulcast endpoints ---

  /** Create RTMP simulcast destination. Returns updated simulcast list. */
  static async createSimulcastRtmp(cId: number, mediaId: number, req: ISimulcastRtmpUpdateReq): Promise<ISimulcastAllData[]> {
    return webApi.post<ISimulcastAllData[]>(`media/${mediaId}/stream/simulcast/rtmp`, { cId }, req);
  }

  /** Update RTMP simulcast destination. Returns updated simulcast list. */
  static async updateSimulcastRtmp(cId: number, mediaId: number, simulcastId: number, req: ISimulcastRtmpUpdateReq): Promise<ISimulcastAllData[]> {
    return webApi.post<ISimulcastAllData[]>(`media/${mediaId}/stream/simulcast/rtmp/${simulcastId}`, { cId }, req);
  }

  /** Create Facebook simulcast destination. Returns single simulcast. */
  static async createSimulcastFB(cId: number, mediaId: number, req: ISimulcastFBCreateReq): Promise<ISimulcastAllData> {
    return webApi.post<ISimulcastAllData>(`media/${mediaId}/stream/simulcast/fb`, { cId }, req);
  }

  /** Update Facebook simulcast destination. Returns single simulcast. */
  static async updateSimulcastFB(cId: number, mediaId: number, simulcastId: number, req: ISimulcastFBUpdateReq): Promise<ISimulcastAllData> {
    return webApi.post<ISimulcastAllData>(`media/${mediaId}/stream/simulcast/fb/${simulcastId}`, { cId }, req);
  }

  /** Create YouTube simulcast destination. Returns single simulcast. */
  static async createSimulcastYT(cId: number, mediaId: number, req: ISimulcastYTCreateReq): Promise<ISimulcastAllData> {
    return webApi.post<ISimulcastAllData>(`media/${mediaId}/stream/simulcast/yt`, { cId }, req);
  }

  /** Update YouTube simulcast destination. Returns single simulcast. */
  static async updateSimulcastYT(cId: number, mediaId: number, simulcastId: number, req: ISimulcastYTUpdateReq): Promise<ISimulcastAllData> {
    return webApi.post<ISimulcastAllData>(`media/${mediaId}/stream/simulcast/yt/${simulcastId}`, { cId }, req);
  }

  /** Delete simulcast destination. Returns remaining simulcast list. */
  static async deleteSimulcast(cId: number, mediaId: number, simulcastId: number): Promise<ISimulcastAllData[]> {
    return webApi.delete<ISimulcastAllData[]>(`media/${mediaId}/stream/simulcast/${simulcastId}`, { cId });
  }

  /** Start simulcast destination. Void response (client patches status). */
  static async startSimulcast(cId: number, mediaId: number, simulcastId: number): Promise<void> {
    return webApi.post(`media/${mediaId}/stream/simulcast/${simulcastId}/start`, { cId }, {});
  }

  /** Stop simulcast destination. Void response (client patches status). */
  static async stopSimulcast(cId: number, mediaId: number, simulcastId: number): Promise<void> {
    return webApi.post(`media/${mediaId}/stream/simulcast/${simulcastId}/stop`, { cId }, {});
  }
}
